/**
 * StoreUpdateRequiredModal — blocking modal for the `mandatory` store-update tier.
 *
 * Rendered when the running native version is below `minMandatoryVersion`. Non-dismissible:
 * no close X, Esc is inert, backdrop click is inert. The only exit is the CTA, which opens
 * the store listing in a new context (`window.open`, same as the banner). The user is expected
 * to leave the app, install the update from the store, and reopen — at which point the
 * running version is above the floor and the modal never mounts.
 *
 * Built on Radix Dialog primitives directly, mirroring `InitialLoginOnboardingModal` — see
 * that file for the anti-close event handlers and portal/z-index reasoning.
 */

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ArrowDownToLine } from 'lucide-react';
import { useStoreUpdate } from '@/hooks/useStoreUpdate';

export function StoreUpdateRequiredModal() {
  const { tier, storeUrl, latestVersion, runningVersion } = useStoreUpdate();

  if (tier !== 'mandatory') return null;
  if (!storeUrl) return null;

  const handleUpdate = () => {
    window.open(storeUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <DialogPrimitive.Root open>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" />
        <DialogPrimitive.Content
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <ArrowDownToLine className="w-5 h-5 text-blue-600" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogPrimitive.Title className="text-lg font-bold text-gray-900">
                Update required
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-sm text-gray-600 mt-1">
                This version of Trade Loop is no longer supported.
                {latestVersion ? ` Update to ${latestVersion} to continue.` : ' Please update to continue.'}
              </DialogPrimitive.Description>
            </div>
          </div>

          {runningVersion && (
            <p className="text-xs text-gray-500 mb-4">
              You are on version {runningVersion}.
            </p>
          )}

          <button
            type="button"
            onClick={handleUpdate}
            className="w-full bg-blue-600 text-white font-semibold px-4 py-2.5 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Open store to update
          </button>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export default StoreUpdateRequiredModal;
